import {
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Box
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { scrollToTop, scrollToTopSticky } from '../../../helpers';
import { ISpec } from '../../../models';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { GlobalContext } from '../../../context/global';

interface IListOptions {
  spec: ISpec;
  contractCategories: string[];
  tenDayCategories: string[];
  selectedCategoryIndex: number;
  setSelectedCategoryIndex: (value: React.SetStateAction<number>) => void;
  setCategoriesDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  missingExteriorColorScheme: boolean;
}
export const ListOptions: FC<IListOptions> = ({
  spec,
  contractCategories,
  tenDayCategories,
  selectedCategoryIndex,
  setSelectedCategoryIndex,
  setCategoriesDrawerOpen,
  missingExteriorColorScheme
}) => {
  return (
    <>
      <Typography color="primary" variant="h6">
        Contract Options
      </Typography>
      {spec?.missingContractCategories !== null && (
        <Typography color="error" variant="caption" sx={{ display: 'block', marginTop: '-2px' }}>
          Missing required options
        </Typography>
      )}
      <List>
        {contractCategories.map((option, index) => {
          const isInvalid =
            spec?.missingContractCategories?.filter(cat => cat.category === option).length > 0;
          return (
            <ListItem disablePadding key={`${index}`}>
              <ListItemButton
                sx={{ color: theme => (isInvalid ? theme.palette.error.main : 'inherit') }}
                selected={selectedCategoryIndex === index}
                onClick={() => {
                  scrollToTop();
                  setSelectedCategoryIndex(index);
                  setCategoriesDrawerOpen(false);
                }}
              >
                <ListItemText primary={option} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Typography color="primary" variant="h6">
        Ten Day Options
      </Typography>

      {spec?.missingTenDayCategories !== null && (
        <Typography color="error" variant="caption" sx={{ display: 'block', marginTop: '-2px' }}>
          Missing required options
        </Typography>
      )}
      <List>
        {tenDayCategories.map((option, index) => {
          const isInvalid =
            spec?.missingTenDayCategories?.filter(cat => cat.category === option).length > 0;
          return (
            <ListItem disablePadding key={`${index}`}>
              <ListItemButton
                sx={{ color: theme => (isInvalid ? theme.palette.error.main : 'inherit') }}
                selected={selectedCategoryIndex === index + contractCategories.length}
                onClick={() => {
                  if (option === 'Color Scheme Options' && missingExteriorColorScheme) {
                    return null;
                  } else {
                    scrollToTop();
                    setSelectedCategoryIndex(index + contractCategories.length);
                    setCategoriesDrawerOpen(false);
                  }
                }}
              >
                <ListItemText
                  primary={option}
                  secondary={
                    option === 'Color Scheme Options' && missingExteriorColorScheme
                      ? 'Must Select Exterior Color Scheme'
                      : ''
                  }
                  sx={{
                    '.MuiListItemText-secondary': {
                      fontSize: '12px'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  );
};

interface IAccordionListOptions extends IListOptions {
  isTenDayCategorySelected: boolean;
}

export const AccordionListOptions: FC<IAccordionListOptions> = ({
  spec,
  contractCategories,
  tenDayCategories,
  selectedCategoryIndex,
  setSelectedCategoryIndex,
  setCategoriesDrawerOpen,
  missingExteriorColorScheme,
  isTenDayCategorySelected
}) => {
  const classes = useStyles();

  const [contractOptionsExpanded, setContractOptionsExpanded] = useState(!isTenDayCategorySelected);
  const [tenDayOptionsExpanded, setTenDayOptionsExpanded] = useState(isTenDayCategorySelected);

  useEffect(() => {
    setContractOptionsExpanded(!isTenDayCategorySelected);
    setTenDayOptionsExpanded(isTenDayCategorySelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTenDayCategorySelected]);

  const contentRef = useContext(GlobalContext).contentRef;

  const expandContractOptions = () => {
    isTenDayCategorySelected
      ? setContractOptionsExpanded(!contractOptionsExpanded)
      : setContractOptionsExpanded(true);
  };

  const expandTenDayOptions = () => {
    isTenDayCategorySelected
      ? setTenDayOptionsExpanded(true)
      : setTenDayOptionsExpanded(!tenDayOptionsExpanded);
  };
  return (
    <>
      <Accordion
        disableGutters
        expanded={contractOptionsExpanded}
        onClick={expandContractOptions}
        sx={{ boxShadow: 'none' }}
      >
        <AccordionSummary sx={{ padding: 0 }} classes={{ content: classes.content }}>
          <Typography color="primary" variant="h6">
            Contract Options
          </Typography>

          <IconButton onClick={expandContractOptions}>
            {contractOptionsExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
          <Box sx={{ flexBasis: '100%', height: 0 }}></Box>
          {spec?.missingContractCategories !== null && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: 'block', marginTop: '-2px' }}
            >
              Missing required options
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <List>
            {contractCategories.map((option, index) => {
              const isInvalid =
                spec?.missingContractCategories?.filter(cat => cat.category === option).length > 0;
              return (
                <ListItem disablePadding key={`${index}`}>
                  <ListItemButton
                    sx={{
                      color: theme => (isInvalid ? theme.palette.error.main : 'inherit'),
                      padding: 0
                    }}
                    selected={selectedCategoryIndex === index}
                    onClick={() => {
                      scrollToTopSticky(contentRef);
                      setSelectedCategoryIndex(index);
                      setCategoriesDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={option} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
      <Accordion
        disableGutters
        expanded={tenDayOptionsExpanded}
        onClick={expandTenDayOptions}
        sx={{ boxShadow: 'none' }}
      >
        <AccordionSummary sx={{ padding: 0 }} classes={{ content: classes.content }}>
          <Typography color="primary" variant="h6">
            Ten Day Options
          </Typography>
          <IconButton onClick={expandTenDayOptions}>
            {tenDayOptionsExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
          <Box sx={{ flexBasis: '100%', height: 0 }}></Box>
          {spec?.missingTenDayCategories !== null && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: 'block', marginTop: '-2px' }}
            >
              Missing required options
            </Typography>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ padding: 0, margin: 0 }}>
          <List>
            {tenDayCategories.map((option, index) => {
              const isInvalid =
                spec?.missingTenDayCategories?.filter(cat => cat.category === option).length > 0;
              return (
                <ListItem disablePadding key={`${index}`}>
                  <ListItemButton
                    sx={{
                      color: theme => (isInvalid ? theme.palette.error.main : 'inherit'),
                      padding: 0
                    }}
                    selected={selectedCategoryIndex === index + contractCategories.length}
                    onClick={() => {
                      if (option === 'Color Scheme Options' && missingExteriorColorScheme) {
                        return null;
                      } else {
                        scrollToTopSticky(contentRef);
                        setSelectedCategoryIndex(index + contractCategories.length);
                        setCategoriesDrawerOpen(false);
                      }
                    }}
                  >
                    <ListItemText
                      primary={option}
                      secondary={
                        option === 'Color Scheme Options' && missingExteriorColorScheme
                          ? 'Must Select Exterior Color Scheme'
                          : ''
                      }
                      sx={{
                        '.MuiListItemText-secondary': {
                          fontSize: '12px'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
const useStyles = makeStyles<Theme>(theme => ({
  content: {
    justifyContent: 'space-between',
    margin: theme.spacing(0.25, 0),
    flexWrap: 'wrap'
  }
}));
